// Call detail page — slim orchestrator. Each major section is a sub-component
// in /pages/call-detail/.
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { useAuth } from '../contexts/AuthContext';
import { RecordingPlayer } from './call-detail/RecordingPlayer';
import { CallHeader } from './call-detail/CallHeader';
import { CallInfoCard } from './call-detail/CallInfoCard';
import { VoiceFabricTraceCard } from './call-detail/VoiceFabricTraceCard';
import { TranscriptionCard } from './call-detail/TranscriptionCard';
import { GradingSidebar } from './call-detail/GradingSidebar';
import { SmokeballPanel } from './call-detail/SmokeballPanel';
import { DiscTipsMiniCard } from './call-detail/DiscTipsMiniCard';
import RelatedCallsCard from './call-detail/RelatedCallsCard';
import WinBanner from './call-detail/WinBanner';
import AttributionCard from './call-detail/AttributionCard';
import PrintBrandingFooter from './call-detail/PrintBrandingFooter';
import JourneyTimelineCard from './call-detail/JourneyTimelineCard';
import ScoreBreakdownCard from './call-detail/ScoreBreakdownCard';
import CoachingPlaybookCard from './call-detail/CoachingPlaybookCard';
import SmsHistoryStrip from './call-detail/SmsHistoryStrip';
import ArchetypePlaybookCard from './call-detail/ArchetypePlaybookCard';
import GymSalesJourneyCard from './call-detail/GymSalesJourneyCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallDetailPage = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [call, setCall] = useState(null);
  const [grading, setGrading] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCallDetails = useCallback(async () => {
    try {
      const [callResponse, gradingResponse] = await Promise.all([
        axios.get(`${API}/calls/${callId}`),
        axios.get(`${API}/calls/${callId}/grading`).catch(() => null)
      ]);
      setCall(callResponse.data);
      if (gradingResponse) {
        setGrading(gradingResponse.data);
      }
    } catch {
      toast.error('Failed to load call details');
    } finally {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => {
    if (callId) fetchCallDetails();
  }, [callId, fetchCallDetails]);

  if (loading) {
    return <div className="text-center py-12 text-slate-600">Loading...</div>;
  }
  if (!call) {
    return <div className="text-center py-12 text-slate-600">Call not found</div>;
  }

  return (
    <div className="space-y-6" data-testid="call-detail-page">
      <CallHeader call={call} grading={grading} onBack={() => navigate(-1)} />
      <AttributionCard call={call} onReassigned={fetchCallDetails} />
      <WinBanner call={call} onWon={fetchCallDetails} />
      <CallInfoCard call={call} />

      {/* GymSales journey — full CRM history for the caller, above the grid. */}
      <GymSalesJourneyCard callId={callId} />

      {grading && <JourneyTimelineCard grading={grading} />}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Center: recording, transcript, then VF actions log */}
        <div className="lg:col-span-2 space-y-6">
          <RecordingPlayer callId={callId} />
          <TranscriptionCard
            transcription={call.transcription}
            callId={callId}
            canRetranscribe={user?.role === 'master_admin' && !!call.recording_url}
            onUpdated={fetchCallDetails}
            agentId={call?.agent_id}
            agentName={call?.agent_name}
            domainName={call?.domain_name}
            customerPhone={call?.customer_phone}
          />
          {['master_admin', 'domain_admin', 'manager', 'admin',
            'domain_supervisor', 'supervisor'].includes(user?.role) && (
            <VoiceFabricTraceCard
              callId={callId}
              token={token}
              onOpenFullPage={() => navigate(`/calls/${callId}/voicefabric`)}
            />
          )}
          <RelatedCallsCard callId={callId} phone={call?.customer_phone} />
        </div>

        {/* Right: grading */}
        <div className="space-y-6">
          <DiscTipsMiniCard callId={callId} callerPhone={call?.customer_phone} />
          <ArchetypePlaybookCard callId={callId} />
          <GradingSidebar
            grading={grading}
            playbookAvailable={!!(call?.customer_phone && grading?.overall_score != null)}
          />
          {grading && <ScoreBreakdownCard grading={grading} />}
          {call?.transcription && <SmokeballPanel call={call} />}
        </div>
      </div>

      {/* Coaching playbook — full width under the grading grid */}
      <SmsHistoryStrip callId={callId} />
      {grading && <CoachingPlaybookCard callId={callId} call={call} grading={grading} />}
      {/* Print-only branded footer (per-tenant logo + accent). Hidden on
          screen; visible on the printed PDF. Auto-suppresses the default
          CSS footer via the .has-custom-print-brand body class. */}
      <PrintBrandingFooter />
    </div>
  );
};

export default CallDetailPage;
